import {
  mdiClose,
  mdiContentCopy,
  mdiInformationOutline,
  mdiSquareEditOutline,
  mdiTrashCanOutline,
  mdiTune,
  mdiFilterOutline,
  mdiAccountGroupOutline, mdiMonitorScreenshot,
} from '@mdi/js';
import Icon from '@mdi/react';
import {
  Card,
  Col,
  Collapse,
  Form,
  Row,
  Skeleton,
  Space,
  Spin,
  Tag, Tooltip,
} from 'antd';
import { isString } from 'lodash';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, {
  memo,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { UI54Card, UI54Modal } from '@agpl/ui54kit';
import { useTranslation } from 'react-i18next';
import AddActorToBanGroupBtn from '../AddActorToBanGroupBtn';
import AntButtonWithMDI from '../../../../components/AntButtonWithMDI';
import DateLabelFormat54origins from '../../../../54origins/components/DateLabelFormat54origins';
import DeleteButton from '../../../../components/DeleteButton';
import GroupInfoFormItems from '../GroupInfoFormItems';
import InternalOrExternalTag from '../../InternalOrExternalTag';
import SendActorBtn from '../../SendActorBtn';
import ServiceInfoFormItems from '../ServiceInfoFormItems';
import UserInfoFormItems from '../UserInfoFormItems';
import UtilityBtnForActorInfo from '../UtilityBtnForActorInfo';
import AddActorsForActorBtn from '../AddActorsForActorBtn';
import './ActorInfo.scss';

import {
  textToClipboard,
  capitalizeAndTranslateMsg, capitalize, validateNotOnlySpaces, validateNoEmoji,
} from '../../../../mainUtils';
import {
  getActorsInfo, getActorsInfoModal, getDefaultAdminGroupUUID, getDefaultBanGroupUUID,
  getDefaultGroupNames,
  isActorsFetching,
  isInfoFetching,
} from '../../../selectors';
import { getNameForActor } from '../../../../54origins/utils54origins';
import useAuthActorsRequests from '../../../hooks/useAuthActorsRequests';
import useURLParams from '../../../../hooks/useURLParams';
import { getMeIsRootOrAdmin } from '../../../../profile/selectors';
import ActorListOfTags from '../../../ActorListOfTags';
import BaseCard from '../../../../_ui/BaseCard/BaseCard';
import BaseIcon from '../../../../_ui/BaseIcon/BaseIcon';
import BaseTag from '../../../../_ui/BaseTag/BaseTag';
import PermissionsPage from '../../../../permissions/components/PermissionsPage';
import BaseButton from '../../../../_ui/BaseButton/BaseButton';
import PublicInterfacesModalPage from '../../../../publicInterfaces/components/PublicInterfacesModalPage';

function ActorInfo({
  onSuccessUpdate,
  onSuccessDelete,
  applyFilter,
  hideElements = [],
  isCollapsed = false,
  switchView,
  modalView,
  modalActorUUID,
}) {
  const { t } = useTranslation();

  const [actorForm] = Form.useForm();

  const { requestGetActorOnAuth, requestGetActorOnAuthForModal } = useAuthActorsRequests();

  const {
    getURLParams, routeNavigateAndClearParams, clearSearchParams, setSearchParams,
  } = useURLParams();

  const actorData = useSelector(getActorsInfo);
  const actorModalData = useSelector(getActorsInfoModal);
  const isFetching = useSelector(isInfoFetching);
  const isActorListFetching = useSelector(isActorsFetching);
  const defaultGroupNames = useSelector(getDefaultGroupNames);
  const meIsRootOrAdmin = useSelector(getMeIsRootOrAdmin);
  const adminGroupUUID = useSelector(getDefaultAdminGroupUUID);
  const banGroupUUID = useSelector(getDefaultBanGroupUUID);

  const [editMode, setEditMode] = useState(false);
  const [fieldsIsChanged, setFieldsIsChanged] = useState(false);

  const [collapsed, setCollapsed] = useReducer((state) => !state, false);

  const [permsModalOpen, setPermsModalOpen] = useState(false);

  const [publicInterfacesModalOpen, setPublicInterfacesModalOpen] = useState(false);

  const {
    groups: urlGroups,
    actor_type: urlActorType,
    uuid: actorURLUUID,
  } = getURLParams();

  const {
    initial_key = '',
    root_perms_signature: rootPermsSignature = false,
    uuid: actorUUID,
    created,
    actor_type: actorType,
    uinfo: {
      birthday = null,
      date_updated: dateUpdated,
      description = '',
      email = '',
      first_name = '',
      group_name = '',
      groups = [],
      internal_service_domain = '',
      internal_user: internal = false,
      isRobot = false,
      isGroupImportant = false,
      last_name = '',
      listing_group = '',
      login = '',
      password: actorPassword,
      phone_number = '',
      public_interfaces = [],
      reserve_domains = '',
      service_domain = '',
      service_name = '',
      weight = '',
    } = {},
  } = modalView ? actorModalData : actorData || {};

  const isUserBanned = groups?.includes(banGroupUUID);

  const isUser = actorType === 'user' || actorType === 'classic_user';
  const isService = actorType === 'service';
  const isGroup = actorType === 'group';

  const serviceInitialValues = {
    description,
    groups,
    initial_key,
    listing_group,
    service_domain,
    service_name,
    internal_service_domain,
    reserve_domains,
    internal,
  };

  const userInitialValues = {
    birthday: birthday ? dayjs(birthday, 'YYYY-MM-DD') : null,
    email,
    first_name,
    groups,
    initial_key,
    internal,
    isRobot,
    last_name,
    login,
    phone_number,
    newKeyPair: false,
    typeOfUser: actorType,
  };

  const groupInitialValues = {
    group_name,
    weight,
    description,
    internal,
    public_interfaces,
    isGroupImportant,
  };

  const filterActive = isString(urlGroups)
    && urlGroups === actorURLUUID
    && !urlActorType;

  const defaultTranslatePath = 'auth.validation.messages.';

  const validateMessages = {
    required: capitalizeAndTranslateMsg('required', 'is required'),
    string: {
      len: capitalizeAndTranslateMsg(`${defaultTranslatePath}len`, 'must be exactly ${len} characters'),
      min: capitalizeAndTranslateMsg(`${defaultTranslatePath}min`, 'must be at least ${min} characters'),
      max: capitalizeAndTranslateMsg(`${defaultTranslatePath}max`, 'cannot be longer than ${max} characters'),
    },
    types: {
      email: capitalizeAndTranslateMsg(`${defaultTranslatePath}email`, 'is not a valid email'),
      number: capitalizeAndTranslateMsg(`${defaultTranslatePath}number`, 'must be a number type'),
      url: capitalizeAndTranslateMsg(`${defaultTranslatePath}url`, 'is not a valid URL'),
    },
  };

  const isRequired = {
    required: true,
    whitespace: true,
    message: 'The field is required and cannot be empty',
  };

  const rules = {
    user: {
      first_name: [{ max: 50 }, isRequired, { validator: validateNotOnlySpaces }, { validator: validateNoEmoji }],
      last_name: [{ max: 50 }, isRequired, { validator: validateNotOnlySpaces }, { validator: validateNoEmoji }],
      password: [{ min: 4 }, { max: 50 }, isRequired, { validator: validateNotOnlySpaces }, { validator: validateNoEmoji }],
      password_confirmation: [
        {
          required: true,
          message: 'Please confirm your password!',
        },
        ({ getFieldValue }) => ({
          validator(_, value) {
            if (!value || getFieldValue('password') === value) {
              return Promise.resolve();
            }
            return Promise.reject(new Error('The two passwords that you entered do not match!'));
          },
        }),
      ],
      login: [{ min: 3 }, { max: 36 }, { validator: validateNotOnlySpaces }, { validator: validateNoEmoji }],
      initial_key: [{ len: 130 }],
      phone_number: [{
        pattern: /^[0-9 ()+-]+$/,
        message: 'Phone number is not valid',
      }],
    },
    group: {
      group_name: [isRequired, { min: 1 }, { max: 50 }, { validator: validateNotOnlySpaces }, { validator: validateNoEmoji }],
      weight: [{ type: 'number' }, {
        required: true,
        message: 'The field is required and cannot be empty',
      }],
    },
    service: {
      service_name: [isRequired, { min: 1 }, { max: 50 }, { validator: validateNotOnlySpaces }, { validator: validateNoEmoji }],
      service_domain: [{ type: 'url' }, isRequired, { validator: validateNotOnlySpaces }, { validator: validateNoEmoji }],
      initial_key: [{ len: 130 }, isRequired],
    },
  };

  const isDefault = isGroup && defaultGroupNames.includes(group_name);

  const additionalTag = () => (
    <>
      {isDefault && (
      <BaseTag className="tag-gray ml-2">
        {capitalizeAndTranslateMsg('auth.headers.default', 'default').toUpperCase()}
      </BaseTag>
      )}
    </>
  );

  const onApplyFilter = () => {
    applyFilter([
      {
        key: 'uinfo.groups',
        value: actorURLUUID,
        label: 'Group with perms 2',
        className: '',
      },
    ]);
  };

  const getActorInitFormValues = () => {
    switch (actorType) {
      case 'classic_user':
      case 'user':
      case 'phantom':
        return userInitialValues;
      case 'group':
        return groupInitialValues;
      case 'service':
        return serviceInitialValues;
      default:
        return [];
    }
  };

  const editBtnOnClick = useCallback(() => {
    setEditMode(true);
  }, []);

  const onSavePublicInterfaceCallback = () => initFunc();

  const setInitActorForm = () => {
    const data = {
      ...getActorInitFormValues(),
      password: actorPassword,
      uuid: actorUUID,
      removeActorsList: [],
      addActorsList: [],
    };

    actorForm.setFieldsValue(data);
  };

  const onCancel = () => {
    setEditMode(false);
    setFieldsIsChanged(false);

    setInitActorForm();
  };

  const updateActorCallback = ({ addActorsList, removeActorsList }) => {
    if (editMode && (addActorsList || removeActorsList)) {
      actorForm.setFieldsValue({
        addActorsList,
        removeActorsList,
      });

      setFieldsIsChanged(true);
    } else {
      initFunc();
      onSuccessUpdate?.();
    }
  };

  const getActorForm = () => {
    const infoProps = {
      actorForm,
      actorType,
      editBtnOnClick,
      setFieldsIsChanged,
      editMode,
      actorData: modalView ? actorModalData : actorData,
      actorUUID,
      validateMessages,
      onSuccessUpdate,
    };

    switch (actorType) {
      case 'classic_user':
      case 'user':
      case 'phantom':
        return (
          <UserInfoFormItems
            {...infoProps}
            rules={rules.user}
            hideElements={['oldPassword', 'passwordConfirmation']}
          />
        );
      case 'group':
        return (
          <GroupInfoFormItems
            {...infoProps}
            rules={rules.group}
          />
        );
      case 'service':
        return (
          <ServiceInfoFormItems
            {...infoProps}
            rules={rules.service}
            onSuccessActorUpdate={onSuccessUpdate}
          />
        );
      default:
        return null;
    }
  };

  const checkIsNotHide = (name) => !hideElements.includes(name);

  const initFunc = async () => {
    if (modalView) {
      await requestGetActorOnAuthForModal({ uuid: modalActorUUID })
        .then(() => {})
        .catch(() => clearSearchParams(['uuid']));
    } else {
      await requestGetActorOnAuth({ uuid: actorURLUUID })
        .then(() => {
        })
        .catch(() => clearSearchParams(['uuid']));
    }
  };

  useEffect(() => {
    if ((actorURLUUID) && (actorURLUUID !== actorUUID)) {
      initFunc();
    }

    onCancel();
  }, [actorURLUUID]);

  useEffect(() => {
    if ((modalActorUUID) && (modalActorUUID !== actorUUID)) {
      initFunc();
    }

    onCancel();
  }, [modalActorUUID]);

  useEffect(() => {
    setInitActorForm();
  }, [actorUUID, editMode]);

  return (
    <Spin
      spinning={isFetching || isActorListFetching || !actorUUID}
    >
      <Form
        layout="vertical"
        autoComplete="off"
        form={actorForm}
        validateMessages={validateMessages}
        onFieldsChange={() => {
          setFieldsIsChanged(true);
        }}
      >
        <UI54Card
          className={`${isCollapsed ? '' : 'border-blue'}`}
          actions={isCollapsed ? [<CollapseBtn onClick={setCollapsed} />] : null}
          noBodyPadding
          variant={`${isCollapsed ? 'default' : 'bordered'}`}
        >
          <Row
            gutter={[16, 0]}
            wrap={false}
            style={{
              padding: '16px',
            }}
          >
            <Col span={6}>
              <h4 className="header-primary">
                {capitalizeAndTranslateMsg('auth.headers.actor_info', 'actor info')}
              </h4>
            </Col>
            {!modalView && (
              <Col span={18}>
                <Space
                  size="small"
                  className="w-full justify-end"
                  align="start"
                >
                  {checkIsNotHide('closeBtn') && (
                    <AntButtonWithMDI
                      className="button-warning-outlined"
                      size="small"
                      onClick={() => switchView()}
                      pathForMdi={mdiClose}
                      label={capitalize(t('auth.buttons.close', 'close'))}
                    />
                  )}
                  {/* {isGroup && checkIsNotHide('applyFilterBtn') && ( */}
                  {/*  <AntButtonWithMDI */}
                  {/*    className="button-secondary-outlined" */}
                  {/*    size="small" */}
                  {/*    onClick={onApplyFilter} */}
                  {/*    disabled={isFetching || filterActive} */}
                  {/*    label={capitalizeAndTranslateMsg('auth.buttons.apply_filter', 'apply filter')} */}
                  {/*    pathForMdi={mdiFilterOutline} */}
                  {/*    iconClassName="mr-1" */}
                  {/*  /> */}
                  {/* )} */}
                  {editMode && (
                    <>
                      <AntButtonWithMDI
                        className="button-secondary-outlined"
                        size="small"
                        onClick={onCancel}
                        disabled={isDefault || isFetching}
                        pathForMdi={mdiClose}
                        iconClassName="mr-1"
                        label={capitalizeAndTranslateMsg('auth.buttons.cancel', 'cancel')}
                      />
                      <SendActorBtn
                        actorForm={actorForm}
                        actorType={actorType}
                        disabled={isDefault || isFetching || !fieldsIsChanged}
                        mode="update"
                        actorUUID={modalView ? modalActorUUID : actorURLUUID}
                        btnSize="small"
                        onSubmitCallback={onSuccessUpdate}
                        onFinallyCallback={onCancel}
                        fieldsIsChanged={fieldsIsChanged}
                      />
                    </>
                  )}
                  {!editMode && checkIsNotHide('editBtn') && (
                    <AntButtonWithMDI
                      className="button-primary-outlined"
                      size="small"
                      disabled={isDefault || isFetching}
                      pathForMdi={mdiSquareEditOutline}
                      onClick={editBtnOnClick}
                      label={capitalizeAndTranslateMsg('54origins.verb.edit', 'edit')}
                    />
                  )}
                </Space>
              </Col>
            )}
          </Row>
          <hr className="m-0" />
          <Row
            className={`${isUserBanned ? 'bg-red' : ''} border-0`}
            style={{
              padding: '16px',
            }}
            gutter={[16, 0]}
          >
            <Col span={16}>
              <Row>
                <Col
                  span={24}
                  className="flex items-center"
                >
                  <Skeleton
                    loading={isFetching}
                    paragraph={false}
                    title={{
                      width: '50%',
                      style: {
                        margin: 0,
                        height: 24,
                      },
                    }}
                    active
                    round
                  >
                    <Tooltip title={getNameForActor(modalView ? actorModalData : actorData)}>
                      <h3 className="header-primary m-0 truncate">
                        {getNameForActor(modalView ? actorModalData : actorData)}
                      </h3>
                    </Tooltip>

                    <Space className="ml-2" align="start">
                      <InternalOrExternalTag internal={internal} />

                      <BaseTag className={`tag-${actorType}`}>
                        {capitalizeAndTranslateMsg(`auth.headers.${actorType}`, actorType)}
                      </BaseTag>

                      {additionalTag()}

                      {/* <RobotActorTag isRobot={isRobot} /> */}

                      <ActorListOfTags actor={modalView ? actorModalData : actorData} isUserBanned={isUserBanned} />
                    </Space>
                  </Skeleton>
                </Col>
                <Col
                  span={24}
                  className="flex items-center mt-2"
                >
                  <h5 className="header-primary">
                    UUID:
                    {' '}
                    {modalView ? modalActorUUID : actorURLUUID}
                  </h5>
                  <AntButtonWithMDI
                    className="button-primary-link ml-2"
                    size="small"
                    onClick={() => {
                      if (modalView) {
                        textToClipboard(modalActorUUID);
                      } else {
                        textToClipboard(actorURLUUID);
                      }
                    }}
                    pathForMdi={mdiContentCopy}
                  />
                </Col>
              </Row>
            </Col>
            <Col span={8} className="ml-auto">
              <Row>
                <Col
                  span={24}
                  className="flex"
                >
                  <DateLabelFormat54origins
                    labelBeforeDate="created:"
                    date={created}
                    className="ml-auto"
                    textType="secondary"
                    format="DD-MM-YYYY | HH:mm"
                  />
                </Col>
                {dateUpdated && (
                  <Col
                    span={24}
                    className="flex"
                  >
                    <DateLabelFormat54origins
                      labelBeforeDate="modify:"
                      date={dateUpdated}
                      className="ml-auto"
                      textType="secondary"
                      format="DD-MM-YYYY | HH:mm"
                    />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          {!modalView && (
            <>
              <hr className="m-0" />
              <Row
                style={{
                  padding: '16px',
                }}
              >
                <Col span={24}>
                  <Space align="top" wrap>
                    {actorUUID && (
                      <AddActorsForActorBtn
                        actorData={modalView ? actorModalData : actorData}
                        iconModalPath={mdiAccountGroupOutline}
                        onSaveCallBack={updateActorCallback}
                        editMode={editMode}
                      />
                    )}
                    {isUser && (
                      <AddActorToBanGroupBtn
                        actor={modalView ? actorModalData : actorData}
                        onSaveCallBack={onSuccessUpdate}
                      />
                    )}
                    {checkIsNotHide('permsBtn') && !editMode && actorUUID !== adminGroupUUID && actorUUID !== banGroupUUID && (
                      <AntButtonWithMDI
                        className="button-primary-outlined"
                        // onClick={() => routeNavigateAndClearParams('/admin/actors/permissions', {
                        //   uuid: actorUUID,
                        //   tab: 'permDashboard',
                        // })}
                        onClick={() => {
                          setSearchParams({
                            uuid: actorUUID,
                            tab: 'permDigest',
                          });
                          setPermsModalOpen(true);
                        }}
                        size="small"
                        pathForMdi={mdiTune}
                        sizeMdi={1}
                        iconClassName="mr-2"
                        label={capitalizeAndTranslateMsg('auth.headers.permissions_setting', 'permissions')}
                      />
                    )}

                    {checkIsNotHide('publicInterfacesBtn') && !editMode && actorUUID !== adminGroupUUID && actorUUID !== banGroupUUID && actorType !== 'service' && (
                      <AntButtonWithMDI
                        className="button-primary-outlined"
                        // onClick={() => routeNavigateAndClearParams('/admin/actors/permissions', {
                        //   uuid: actorUUID,
                        //   tab: 'permDashboard',
                        // })}
                        onClick={() => {
                          setSearchParams({
                            uuid: actorUUID,
                          });
                          setPublicInterfacesModalOpen(true);
                        }}
                        size="small"
                        pathForMdi={mdiMonitorScreenshot}
                        sizeMdi={1}
                        iconClassName="mr-2"
                        label={capitalizeAndTranslateMsg('auth.headers.public_interfaces_settings', 'public interfaces')}
                      />
                    )}

                    <UI54Modal
                      open={publicInterfacesModalOpen}
                      onClose={() => setPublicInterfacesModalOpen(false)}
                      onCancel={() => setPublicInterfacesModalOpen(false)}
                      width="1600px"
                      footer={null}
                      destroyOnClose
                    >
                      <PublicInterfacesModalPage hideElements={['closeBtn']} />
                    </UI54Modal>

                    <UI54Modal
                      open={permsModalOpen}
                      onClose={() => setPermsModalOpen(false)}
                      onCancel={() => setPermsModalOpen(false)}
                      width="1600px"
                      footer={null}
                      destroyOnClose
                    >
                      <PermissionsPage hideElements={['closeBtn']} />
                    </UI54Modal>

                    {checkIsNotHide('actorsPermsOnServiceBtn') && !editMode && isService && (
                      <AntButtonWithMDI
                        className="button-primary-outlined"
                        onClick={() => routeNavigateAndClearParams('/admin/actors/perms-on-service', { uuid: actorUUID })}
                        size="small"
                        pathForMdi={mdiTune}
                        sizeMdi={1.2}
                        iconClassName="mr-2"
                        label={capitalizeAndTranslateMsg('auth.headers.permis', 'actors with perms on service')}
                      />
                    )}
                    {isService && meIsRootOrAdmin && (
                      <AntButtonWithMDI
                        size="small"
                        className="button-primary-outlined w-full"
                        onClick={() => {
                          setSearchParams({
                            modal: 'existOnService',
                          });
                        }}
                        label="existing users"
                      />
                    )}

                    {checkIsNotHide('utilityBtn') && (
                      <UtilityBtnForActorInfo
                        actorData={modalView ? actorModalData : actorData}
                        actorType={actorType}
                        actorUUID={actorUUID}
                        onSuccessUpdate={onSuccessUpdate}
                        onSavePublicInterfaceCallback={onSavePublicInterfaceCallback}
                        onSuccessDelete={onSuccessDelete}
                        actorURLUUID={modalView ? modalActorUUID : actorURLUUID}
                        isDefault={isDefault}
                        setPermsModalOpen={setPermsModalOpen}
                      />
                    )}
                  </Space>
                </Col>
              </Row>
            </>
          )}
          <hr className="m-0" />
          {isCollapsed ? (
            <Collapse
              className="card-collapse"
              activeKey={collapsed ? '1' : '0'}
            >
              <Collapse.Panel
                header={capitalizeAndTranslateMsg('auth.headers.detailed_information', 'detailed information')}
                key="1"
                forceRender
              >
                {getActorForm()}
              </Collapse.Panel>
            </Collapse>
          ) : getActorForm()}

          {/* <AddCustomFieldForActor /> */}
        </UI54Card>
      </Form>
    </Spin>
  );
}

export default ActorInfo;

ActorInfo.propTypes = {
  applyFilter: PropTypes.func,
  onSuccessDelete: PropTypes.func,
  onSuccessUpdate: PropTypes.func,
  hideElements: PropTypes.array,
  switchView: PropTypes.func.isRequired,
  modalView: PropTypes.bool,
  modalActorUUID: PropTypes.string,
};

const CollapseBtn = memo(({
  onClick,
}) => (
  <div
    key="go_to_permissions_button"
    className="card-action"
    onClick={onClick}
  >
    <BaseIcon
      path={mdiInformationOutline}
      size={1}
      className="mr-2"
    />
    {capitalizeAndTranslateMsg('auth.headers.detailed_information', 'detailed information')}
  </div>
));
